import type { PatientEncounter } from "@/resources/types";

type DeferredEncounterSnapshot = Pick<PatientEncounter, "id" | "atsScore">;

export interface MevSummaryDeferredState {
  isInitialObsFlowActive: boolean;
  hasInitialObservationSaved: boolean;
  pendingDeferredMevOpen: boolean;
  deferredEncounterId?: string;
  deferredEncounterSnapshot?: DeferredEncounterSnapshot;
}

export const initialMevSummaryDeferredState: MevSummaryDeferredState = {
  isInitialObsFlowActive: false,
  hasInitialObservationSaved: false,
  pendingDeferredMevOpen: false,
  deferredEncounterId: undefined,
  deferredEncounterSnapshot: undefined,
};

const toSnapshot = (
  encounter?: Pick<PatientEncounter, "id" | "atsScore"> | null,
): DeferredEncounterSnapshot | undefined => {
  if (!encounter?.id) return undefined;

  return {
    id: encounter.id,
    atsScore: encounter.atsScore,
  };
};

export const activateMevSummaryDeferredFlow = (
  state: MevSummaryDeferredState,
  encounter?: Pick<PatientEncounter, "id" | "atsScore"> | null,
  options?: {
    openOnExitWithoutObservationSave?: boolean;
  },
): MevSummaryDeferredState => {
  const snapshot = toSnapshot(encounter) ?? state.deferredEncounterSnapshot;
  return {
    ...state,
    isInitialObsFlowActive: true,
    hasInitialObservationSaved: false,
    pendingDeferredMevOpen: !!options?.openOnExitWithoutObservationSave,
    deferredEncounterId: encounter?.id ?? state.deferredEncounterId,
    deferredEncounterSnapshot: snapshot,
  };
};

export const markDeferredObservationSaved = (
  state: MevSummaryDeferredState,
): MevSummaryDeferredState => {
  if (!state.isInitialObsFlowActive) return state;

  return {
    ...state,
    hasInitialObservationSaved: true,
    pendingDeferredMevOpen: true,
  };
};

export const canAttemptDeferredMevOpen = (
  state: MevSummaryDeferredState,
) => {
  return (
    state.pendingDeferredMevOpen &&
    !!(state.deferredEncounterId || state.deferredEncounterSnapshot)
  );
};

export const consumeDeferredMevOpen = (
  state: MevSummaryDeferredState,
): MevSummaryDeferredState => {
  return {
    ...state,
    pendingDeferredMevOpen: false,
  };
};
